import { forwardRef, type ReactNode } from 'react'
import { Theme } from '@mui/material/styles'
import makeStyles from '@mui/styles/makeStyles'
import createStyles from '@mui/styles/createStyles'

export type RecommendationTab = { id: string; title: string; disabled?: boolean }

// Match the actual carousel before its JS is available. Images and the body
// reserve scale with text; taller content can grow without a vertical scroller.
export const recommendationSliderStyles = (theme: Theme) => ({
  position: 'relative' as const,
  overflow: 'hidden',
  padding: theme.spacing(.5, 0, .75),
  '&:before': { content: '""', position: 'absolute' as const, inset: 0, borderRadius: 32, background: 'linear-gradient(180deg, rgba(248, 250, 252, 0.92) 0%, rgba(255, 255, 255, 0) 100%)', pointerEvents: 'none' as const },
  '& .swiper': { overflow: 'hidden', paddingBottom: theme.spacing(1) },
  '& .swiper-wrapper': { alignItems: 'stretch' },
  '& .swiper-slide': { height: 'auto', display: 'flex' },
  '& .swiper:not(.swiper-initialized) .swiper-slide': {
    width: 'calc((100% - 12px) / 2)', marginRight: 12,
    '@media (min-width: 600px)': { width: 'calc((100% - 23.2px) / 2.45)', marginRight: 16 },
    '@media (min-width: 960px)': { width: 'calc((100% - 44.1px) / 3.45)', marginRight: 18 },
    '@media (min-width: 1280px)': { width: 'calc((100% - 69px) / 4.45)', marginRight: 20 },
  },
  '& .swiper-slide > section': { width: '100%', height: '100%' },
  '& .swiper-button-prev, & .swiper-button-next': {
    width: 42, height: 42, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,.96)',
    color: '#1f2933', boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)', top: '42%',
    '&:after': { fontSize: 14, fontWeight: 700 },
    [theme.breakpoints.down('sm')]: { width: 34, height: 34, top: '40%', '&:after': { fontSize: 11 } },
  },
  '& .swiper-button-disabled': { opacity: .3 },
})

const useStyles = makeStyles((theme: Theme) => createStyles({
  frame: {
    '--product-preview-image-height': '10rem',
    '--product-preview-image-ratio': 'auto',
    '--product-preview-image-height-mobile': '10rem',
    '--product-preview-image-ratio-mobile': 'auto',
    '--recommendation-card-body': '18rem',
    '--product-preview-card-min-height': 'calc(var(--product-preview-image-height) + var(--recommendation-card-body))',
    minHeight: 'calc(var(--product-preview-card-min-height) + 18px)',
    display: 'grid', position: 'relative', minWidth: 0, boxSizing: 'border-box',
    '@media (min-width: 960px)': { '--product-preview-image-height': '9.25rem' },
  },
  status: {
    gridArea: '1 / 1', alignSelf: 'start', zIndex: 1,
    padding: theme.spacing(2), boxSizing: 'border-box',
    color: theme.palette.text.primary, backgroundColor: theme.palette.background.paper,
    '& button': { font: 'inherit' },
  },
  content: { gridArea: '1 / 1', minWidth: 0 },
  withContentStatus: { display: 'block' },
  skeletonSlider: recommendationSliderStyles(theme),
  skeletonTrack: { display: 'flex' },
  skeletonSlide: { flexShrink: 0 },
  skeletonCard: {
    width: '100%', minHeight: 'var(--product-preview-card-min-height)',
    boxSizing: 'border-box', border: '1px solid rgba(15,23,42,.08)', borderRadius: 8,
    background: theme.palette.background.paper,
  },
  skeletonImage: { height: 'var(--product-preview-image-height)', background: 'rgba(127,127,127,.08)' },
  skeletonBody: { padding: '1rem', display: 'grid', gap: '.75rem', alignContent: 'start' },
  skeletonLine: { height: '1rem', borderRadius: 4, background: 'rgba(127,127,127,.12)' },
  root: { marginTop: theme.spacing(6), paddingTop: theme.spacing(4), borderTop: '1px solid rgba(15,23,42,.08)' },
  header: { display: 'flex', marginBottom: theme.spacing(1.5), minWidth: 0 },
  tabs: { display: 'flex', gap: theme.spacing(1), flexWrap: 'nowrap', overflowX: 'auto', width: '100%' },
  tab: {
    flexShrink: 0, whiteSpace: 'nowrap', border: `1px solid var(--page-builder-button-bg, ${theme.palette.primary.main})`,
    background: `var(--page-builder-surface, ${theme.palette.background.paper})`,
    color: `var(--page-builder-button-bg, ${theme.palette.primary.main})`,
    borderRadius: 999, padding: '10px 16px', font: 'inherit', cursor: 'pointer',
    '&:disabled': { cursor: 'default' },
    '&:hover': {
      borderColor: `var(--page-builder-button-hover, var(--page-builder-button-bg, ${theme.palette.primary.dark}))`,
      color: `var(--page-builder-button-hover, var(--page-builder-button-bg, ${theme.palette.primary.dark}))`,
    },
  },
  active: {
    background: `var(--page-builder-button-bg, ${theme.palette.primary.main})`,
    color: `var(--page-builder-button-text, ${theme.palette.primary.contrastText})`,
    borderColor: `var(--page-builder-button-bg, ${theme.palette.primary.main})`,
    '&:hover': {
      background: `var(--page-builder-button-hover, var(--page-builder-button-bg, ${theme.palette.primary.dark}))`,
      color: `var(--page-builder-button-text, ${theme.palette.primary.contrastText})`,
    },
  },
  meta: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: theme.spacing(1),
    flexWrap: 'nowrap', whiteSpace: 'nowrap', overflowX: 'auto', lineHeight: 1.5,
    '& > *': { flexShrink: 0 },
    minHeight: '1.5em', marginBottom: theme.spacing(1.25), color: '#64748b', fontSize: '.92rem',
    [theme.breakpoints.down('sm')]: { fontSize: '.86rem' },
  },
}))

export const RecommendationSkeleton = ({ count = 5 }: { count?: number }) => {
  const classes = useStyles()
  return <div className={classes.skeletonSlider} aria-hidden='true' data-recommendation-skeleton>
    <div className='swiper'><div className={`swiper-wrapper ${classes.skeletonTrack}`}>
      {Array.from({ length: Math.max(1, Math.min(5, count)) }, (_, index) => <div key={index} className={`swiper-slide ${classes.skeletonSlide}`}>
        <div className={classes.skeletonCard}>
          <div className={classes.skeletonImage} />
          <div className={classes.skeletonBody}>
            <div className={classes.skeletonLine} style={{ width: '45%' }} />
            <div className={classes.skeletonLine} /><div className={classes.skeletonLine} style={{ width: '80%' }} />
            <div className={classes.skeletonLine} style={{ width: '60%', marginTop: '2rem' }} />
          </div>
        </div>
      </div>)}
    </div></div>
  </div>
}

export const RecommendationCardsFrame = ({ children, loading = false, status, reserve = true, count }: {
  children?: ReactNode; loading?: boolean; status?: ReactNode; reserve?: boolean; count?: number
}) => {
  const classes = useStyles()
  if (!reserve && !children && !status) return null
  return <div className={`${reserve ? classes.frame : ''} ${status && children ? classes.withContentStatus : ''}`} aria-busy={loading || undefined} data-recommendation-frame>
    {status && <div className={classes.status} role='status'>{status}</div>}
    <div className={classes.content}>{children || (reserve && (!status || loading) && <RecommendationSkeleton count={count} />)}</div>
  </div>
}

export const CrossSellRecommendationShell = forwardRef<HTMLElement, {
  tabs: RecommendationTab[]; activeId?: string | null; onSelect?: (id: string) => void;
  meta?: ReactNode; loading?: boolean; status?: ReactNode; reserve?: boolean; children?: ReactNode; minHeight?: number
}>(({ tabs, activeId, onSelect, meta, loading, status, reserve = true, children, minHeight }, ref) => {
  const classes = useStyles()
  if (!reserve && !children && !status) return null
  const visibleTabs = tabs.length ? tabs : [{ id: 'recommendations', title: 'Weitere Empfehlungen' }]
  return <section ref={ref} className={classes.root} style={minHeight ? { minHeight, boxSizing: 'border-box' } : undefined} aria-busy={loading || undefined} data-recommendation-slot='cross-sell'>
    <div className={classes.header}><div className={classes.tabs}>
      {visibleTabs.map(tab => <button key={tab.id} type='button'
        disabled={!onSelect || tab.disabled}
        aria-pressed={tab.id === (activeId || visibleTabs[0].id)}
        className={`${classes.tab} ${tab.id === (activeId || visibleTabs[0].id) ? classes.active : ''}`}
        onClick={() => onSelect?.(tab.id)}>{tab.title}</button>)}
    </div></div>
    <div className={classes.meta}>{meta || <span aria-hidden='true'>&nbsp;</span>}</div>
    <RecommendationCardsFrame loading={loading} status={status} reserve={reserve}>{children}</RecommendationCardsFrame>
  </section>
})
CrossSellRecommendationShell.displayName = 'CrossSellRecommendationShell'
